import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, Search, ShoppingCart, Home, ChevronRight, User, X,
  Heart, Plus, Minus, ShoppingBag, MapPin, Globe,
  ChevronDown, ChevronUp, RefreshCw, Tag, Package, ArrowRight,
  SlidersHorizontal,
  ShoppingBagIcon,
  ShoppingBasket,
} from "lucide-react";

import logo from "../../assets/cosmeta/logo.png";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToPublishedClassified,
  filterLocalProductsByCountry,
  setFilters,
} from "../../redux/classifiedsSlice";
import { addToCart, decrementCartItem } from "../../redux/cartSlice";
import {
  electronicsCategories,
  electronicsSubcategories,
  electronicsProducts,
} from "../CATEGORIES/ElectronicsCategories";
import { CurrencyContext } from "../../GLOBAL/CurrencyContext";
import { Notifications, SupportAgent, ChatBubble, ShoppingCartCheckout, ShoppingCartOutlined, Shop2 } from "@mui/icons-material";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../redux/notificationsSlice";
import {
  electronicsColorAttributes,
  electronicsSizeAttributes,
} from "../ATTRIBUTES/ElectronicsAttributes";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════ */
const T = {
  bg:           "#FFFFFF",
  bgSoft:       "#F7F9FF",
  bgMuted:      "#EEF4FF",
  black:        "#060D1F",
  border:       "#DCE8FF",
  muted:        "#6B8CB8",
  mutedSoft:    "#A8C4D8",
  blue:         "#0A66C2",
  blueLight:    "#5BCBFF",
  blueDim:      "rgba(10,102,194,0.10)",
  gold:         "#FEBA02",
  globalRed:    "#CC0000",
  globalRedDim: "rgba(204,0,0,0.08)",
  grad:         "linear-gradient(135deg,#003580 0%,#0061fe 60%,#FEBA02 100%)",
  gradText:     "linear-gradient(90deg,#0A66C2,#5BCBFF)",
  navy:         "#0A0F1E",
  navyMid:      "#111827",
  orangeOutline:"1.5px solid rgba(10,102,194,0.35)",
};

const GRADIENT = T.grad;
const ACCENT   = T.blue;
const NAVY     = T.navy;
const NAVY_MID = T.navyMid;

// Color palette for the 2×2 category image tiles
const CAT_COLORS = [
  "#0A66C2","#0EA5E9","#0061FE","#003580",
  "#1D4ED8","#2563EB","#1E40AF","#0C4A6E",
  "#065F46","#166534","#7C3AED","#DC2626",
];

/* ═══════════════════════════════════════════════════════════
   GC-STYLE SIDEBAR ACCORDION STYLES
═══════════════════════════════════════════════════════════ */
const A: Record<string, React.CSSProperties> = {
  commerceCard:   { borderRadius: 18, padding: 18, background: "#fff", marginBottom: 16, boxShadow: "0 2px 16px rgba(0,0,0,.06)", border: T.orangeOutline },
  commerceHeader: { fontSize: 18, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: NAVY },
  category:       { padding: 12, borderRadius: 12, background: "#F8FAFC", marginTop: 10, border: "1px solid #F1F5F9", cursor: "pointer" },
  row:            { display: "flex", alignItems: "center", gap: 10, fontWeight: 700 },
  sub:            { paddingLeft: 22, paddingTop: 8 },
  productGroup:   { paddingLeft: 42, paddingTop: 7, paddingBottom: 4, fontWeight: 600, color: "#555", cursor: "pointer", fontSize: 13 },
  sidebar:        {
    background: "rgba(255,255,255,.98)", borderRadius: 20, padding: 20,
    boxShadow: "0 4px 24px rgba(0,0,0,.08)",
    position: "sticky" as const, top: 116,
    maxHeight: "calc(100vh - 130px)", overflowY: "auto" as const,
    border: T.orangeOutline,
  },
};

/* ═══════════════════════════════════════════════════════════
   SORT
═══════════════════════════════════════════════════════════ */
type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";
const SORT_OPTS: { key: SortKey; label: string }[] = [
  { key: "default",    label: "Best Match"       },
  { key: "newest",     label: "New Arrivals"      },
  { key: "price_asc",  label: "Price: Low → High" },
  { key: "price_desc", label: "Price: High → Low" },
  { key: "name_asc",   label: "Name: A → Z"       },
];

/* ═══════════════════════════════════════════════════════════
   GLOBAL TICKER
═══════════════════════════════════════════════════════════ */
const GlobalTicker: React.FC<{ denied: boolean }> = ({ denied }) => {
  const msg = denied
    ? "⚠ LOCATION ACCESS DENIED — SHOWING ALL GLOBAL PRODUCTS · ENABLE LOCATION TO SEE LOCAL LISTINGS · "
    : "◉ GLOBAL MODE ACTIVE — BROWSING WORLDWIDE INVENTORY · TAP LOCAL TO FILTER BY YOUR REGION · ";
  return (
    <div style={{ overflow: "hidden", background: denied ? T.globalRedDim : T.blueDim, borderBottom: `1px solid ${denied ? "rgba(204,0,0,0.25)" : "rgba(10,102,194,0.18)"}`, height: 22, display: "flex", alignItems: "center" }}>
      <style>{`
        @keyframes mc-ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        .mc-ticker{display:inline-block;white-space:nowrap;animation:mc-ticker 24s linear infinite;font-family:'DM Sans',sans-serif;font-size:9px;font-weight:700;letter-spacing:.18em;color:${denied ? T.globalRed : T.blue};padding-left:24px;}
        @keyframes mc-pulse{0%,100%{opacity:1}50%{opacity:.3}}
      `}</style>
      <span className="mc-ticker">{msg.repeat(6)}</span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAGE STYLES (mirrors GeneralCommerce S object)
═══════════════════════════════════════════════════════════ */
const S: Record<string, React.CSSProperties> = {
  page:        { minHeight: "100vh", background: "#F4F6FA", paddingBottom: 90, fontFamily: "'DM Sans',sans-serif" },
  headerTop:   { background: `linear-gradient(180deg,${NAVY} 0%,${NAVY_MID} 100%)`, position: "sticky", top: 0, zIndex: 200, boxShadow: "0 2px 24px rgba(0,0,0,.32)" },
  headerRow1:  { display: "flex", alignItems: "center", gap: 10, padding: "12px 16px 8px" },
  headerRow2:  { display: "flex", alignItems: "center", gap: 8, padding: "0 16px 10px", overflowX: "auto", scrollbarWidth: "none" as any },
  searchWrap:  { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.10)", borderRadius: 10, overflow: "hidden", border: "1.5px solid rgba(255,255,255,.15)", backdropFilter: "blur(8px)", transition: "border-color .2s,box-shadow .2s" },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 14, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn:   { width: 44, minHeight: 42, background: GRADIENT, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  hTextBtn:    { display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px solid rgba(255,255,255,.22)", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 700, padding: "7px 12px", borderRadius: 8, flexShrink: 0, whiteSpace: "nowrap" as const },
  catBtn:      { display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 9, fontWeight: 800, fontSize: 13, cursor: "pointer", border: "1.5px solid rgba(10,102,194,.6)", background: "rgba(10,102,194,.15)", color: T.blueLight, flexShrink: 0 },
  filterChip:  { display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.16)", borderRadius: 8, padding: "7px 13px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" as const, flexShrink: 0 },
  navOverlay:  { position: "fixed" as const, inset: 0, zIndex: 599 },
  navMenu:     { position: "fixed" as const, top: 70, right: 16, width: 230, zIndex: 600, background: "#fff", borderRadius: 18, boxShadow: "0 24px 64px rgba(0,0,0,.20)", border: T.orangeOutline, overflow: "hidden" },
  navItem:     { display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", cursor: "pointer", fontSize: 14, fontWeight: 700, color: NAVY, borderBottom: "1px solid #F1F5F9", background: "none", width: "100%", textAlign: "left" as const, border: "none" },
  sortMenu:    { position: "fixed" as const, background: "#fff", borderRadius: 14, boxShadow: "0 20px 56px rgba(0,0,0,.32)", zIndex: 9999, minWidth: 210, overflow: "hidden", border: T.orangeOutline },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  productsWrap:{ marginBottom: 28 },
  grid:        { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 18 },
  mobileGrid:  { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 14 },
  productCard: { borderRadius: 20, overflow: "hidden", boxShadow: "0 4px 18px rgba(0,0,0,.12)", border: T.orangeOutline, cursor: "pointer", position: "relative" as const, aspectRatio: "3/4", display: "block", background: NAVY },
  imageBox:    { position: "absolute" as const, inset: 0, overflow: "hidden" },
  cartBadge:   { position: "absolute" as const, top: 10, right: 10, zIndex: 10 },
  cartOval:    { width: 38, height: 38, borderRadius: "50%", background: "rgba(10,15,30,.80)", backdropFilter: "blur(12px)", border: "1.5px solid rgba(255,255,255,.25)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(0,0,0,.38)", cursor: "pointer", position: "relative" as const },
  plusCircle:  { position: "absolute" as const, bottom: -5, right: -5, width: 17, height: 17, borderRadius: "50%", background: T.gold, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff", boxShadow: "0 2px 8px rgba(254,186,2,.55)" },
  qtyWrap:     { display: "flex", alignItems: "center", gap: 0, background: "rgba(0,97,254,.92)", borderRadius: 22, padding: "3px 4px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(255,255,255,.2)" },
  qtyBtn:      { width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
  qtyText:     { color: "#fff", fontWeight: 900, minWidth: 22, textAlign: "center" as const, fontSize: 13 },
  mobileTabs:  { position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 64, background: "#fff", display: "flex", justifyContent: "space-around", alignItems: "center", boxShadow: "0 -4px 20px rgba(0,0,0,.10)", zIndex: 100, borderTop: "1px solid #E8ECF0" },
  tabItem:     { display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 3, fontSize: 10, fontWeight: 700, cursor: "pointer", color: "#64748B", padding: "4px 10px" },
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const MetaCommerce = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser         = useSelector((s: RootState) => s.auth.user);
  const authLoaded       = useSelector((s: RootState) => s.auth.authLoaded);
  const userId           = authUser?.userId;
  const notifications    = useSelector((s: RootState) => s.notifications.notifications);
  const unreadCount      = useMemo(() => notifications.filter((n: any) => !n.read).length, [notifications]);
  const listings         = useSelector((s: RootState) => s.classifieds.listings);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const filteredListings = useSelector((s: RootState) => s.classifieds.filteredListings);
  const cartTotal        = cartItems.reduce((s: number, i: any) => s + i.qty, 0);

  const [isMobile,          setIsMobile]         = useState(window.innerWidth <= 768);
  const [catOpen,           setCatOpen]          = useState(false);
  const [desktopCatOpen,    setDesktopCatOpen]   = useState(false);
  const [navOpen,           setNavOpen]          = useState(false);
  const [searchQuery,       setSearchQuery]      = useState("");
  const [searchFocused,     setSearchFocused]    = useState(false);
  const [sortOpen,          setSortOpen]         = useState(false);
  const [activeSort,        setActiveSort]       = useState<SortKey>("default");
  const [activeProductGroup,setActiveProductGroup] = useState<number | null>(null);
  const [openCategories,    setOpenCategories]   = useState<number | null>(null);
  const [openSubCategories, setOpenSubCategories]= useState<number | null>(null);
  const sortBtnRef = useRef<HTMLButtonElement>(null);
  const [sortAnchor, setSortAnchor] = useState<{ top: number; left: number } | null>(null);

  const [userCountry,      setUserCountry]      = useState<string | null>(null);
  const [userCity,         setUserCity]         = useState<string | null>(null);
  const [userLat,          setUserLat]          = useState<number | null>(null);
  const [userLng,          setUserLng]          = useState<number | null>(null);
  const [locationDenied,   setLocationDenied]   = useState(false);
  const [locationLoading,  setLocationLoading]  = useState(true);
  const [locationModalOpen,setLocationModalOpen]= useState(false);
  const [showGlobal,       setShowGlobal]       = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [selectedSize,  setSelectedSize]  = useState<number | null>(null);
  const [variantQty,    setVariantQty]    = useState(1);
  const [variantOpen,   setVariantOpen]   = useState(false);
  const [variantItem,   setVariantItem]   = useState<any | null>(null);
  const [variantMode,   setVariantMode]   = useState<"fashion" | "beauty" | null>(null);
  const [wished,        setWished]        = useState<Set<string>>(new Set());

  const [visibleIds, setVisibleIds] = useState<{
    categories: Set<number>; subs: Set<number>; products: Set<number>;
  }>({ categories: new Set(), subs: new Set(), products: new Set() });

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedClassified()); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId));
    return () => { dispatch(stopUserNotificationsListener()); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!listings.length) { setVisibleIds({ categories: new Set(), subs: new Set(), products: new Set() }); return; }
    const categories = new Set<number>(), subs = new Set<number>(), products = new Set<number>();
    listings.forEach((l: any) => {
      if (l.categoryId)    categories.add(l.categoryId);
      if (l.subcategoryId) subs.add(l.subcategoryId);
      if (l.productId)     products.add(l.productId);
    });
    setVisibleIds({ categories, subs, products });
  }, [listings]);

  useEffect(() => {
    if (!navigator.geolocation) { setLocationDenied(true); setLocationLoading(false); dispatch(filterLocalProductsByCountry(null)); return; }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setUserLng(pos.coords.longitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get   = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country));
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null));
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveProductGroup(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry));
  };

  const toggleSort = () => {
    if (sortOpen) { setSortOpen(false); setSortAnchor(null); return; }
    if (sortBtnRef.current) {
      const r = sortBtnRef.current.getBoundingClientRect();
      setSortAnchor({ top: r.bottom + 6, left: r.left });
    }
    setSortOpen(true);
  };

  useEffect(() => { if (searchQuery.trim()) setActiveProductGroup(null); }, [searchQuery]);

  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const resetFilters = () => {
    setActiveProductGroup(null); setActiveSort("default");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
    dispatch(listenToPublishedClassified());
  };

  const getVariantMode = (item: any): "fashion" | "beauty" | null => {
    if (!item.variants || !Object.keys(item.variants).length) return null;
    if (Object.values(item.variants).some((v: any) => Array.isArray(v.sizeIds) && v.sizeIds.length)) return "fashion";
    if (Object.values(item.variants).some((v: any) => v.imageUrl && (!v.sizeIds || !v.sizeIds.length))) return "beauty";
    return null;
  };

  const canContinue =
    variantMode === null ||
    (variantMode === "fashion" && selectedColor !== null && selectedSize !== null && variantQty > 0) ||
    (variantMode === "beauty"  && selectedColor !== null && variantQty > 0);

  const previewColorId = useMemo(() => {
    if (!variantItem || !variantMode) return null;
    const colors = variantItem.attributes?.[variantMode === "beauty" ? 10001 : 20002];
    if (!Array.isArray(colors) || !colors.length) return null;
    return selectedColor ?? colors[0];
  }, [variantItem, variantMode, selectedColor]);

  useEffect(() => {
    if (variantOpen && variantMode === "beauty" && variantItem && selectedColor === null) {
      const colors = variantItem.attributes?.[10001];
      if (Array.isArray(colors) && colors.length) setSelectedColor(colors[0]);
    }
  }, [variantOpen, variantMode, variantItem, selectedColor]);

  const openProductModal = (item: any) => {
    setVariantItem(item); setVariantMode(getVariantMode(item));
    setSelectedColor(null); setSelectedSize(null); setVariantQty(1); setVariantOpen(true);
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (getVariantMode(item) !== null) { openProductModal(item); return; }
    dispatch(addToCart({ listingId: item.listingId, name: item.name!, price: item.rate!, photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId }));
  };

  const handleVariantContinue = (toCheckout: boolean) => {
    if (!variantItem) return;
    dispatch(addToCart({ listingId: variantItem.listingId, name: variantItem.name!, price: variantItem.rate!, photo: variantItem.variants?.[selectedColor!]?.imageUrl ?? variantItem.photos?.[0], colorId: selectedColor!, ...(variantMode === "fashion" && { sizeId: selectedSize! }), qty: variantQty, hostId: variantItem.hostId, brandId: variantItem.brandId }));
    setVariantOpen(false); setVariantItem(null); setSelectedColor(null); setSelectedSize(null); setVariantQty(1);
    if (toCheckout) goToCheckout();
  };

  const go           = (path: string) => { if (!userId) return; navigate(`/${path}/${userId}`); };
  const goToDetails  = (id: string) => navigate(`/metacommercedetails/${id}`);
  const goToCheckout = () => {
    if (!cartItems.length) { alert("Your cart is empty"); return; }
    navigate(`/metacheckout/${userId}`, { state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0), totalPrice: cartItems.reduce((s: number, i: any) => s + i.qty * i.price, 0), items: cartItems } });
  };

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = filteredListings.filter((l: any) => {
      if (!l.isPublished) return false;
      if (activeProductGroup && l.productId !== activeProductGroup && l.productVariantId !== activeProductGroup) return false;
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
    if (activeSort === "price_asc")  list = [...list].sort((a: any, b: any) => (a.rate ?? 0) - (b.rate ?? 0));
    if (activeSort === "price_desc") list = [...list].sort((a: any, b: any) => (b.rate ?? 0) - (a.rate ?? 0));
    if (activeSort === "name_asc")   list = [...list].sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
    if (activeSort === "newest")     list = [...list].sort((a: any, b: any) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    if (activeSort === "default" && !activeProductGroup) {
      const groups = new Map<number, any[]>();
      list.forEach((l: any) => { const g = groups.get(l.categoryId ?? 0) ?? []; g.push(l); groups.set(l.categoryId ?? 0, g); });
      const capped: any[] = [];
      groups.forEach(g => capped.push(...g.slice(0, 100)));
      return capped;
    }
    return list;
  }, [filteredListings, activeProductGroup, searchQuery, activeSort]);

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty || 0;

  /* ─── ANNOUNCEMENT BAR ─────────────────────────────────── */
  const AnnouncementBar = () => {
    const isGlobal = locationDenied || showGlobal || (!locationLoading && userCountry === null);
    if (isGlobal) return <GlobalTicker denied={locationDenied} />;
    return (
      <div style={{ background: T.grad, color: "#fff", textAlign: "center", fontSize: 10, padding: "7px 0", letterSpacing: "0.18em", fontFamily: "'DM Sans',sans-serif", fontWeight: 600 }}>
        FREE WORLDWIDE SHIPPING ON ORDERS OVER $300
        {userCity && <span style={{ marginLeft: 16, opacity: 0.7 }}>· Near {userCity}</span>}
      </div>
    );
  };

  /* ─── HEADER ROW 1 (FilterRow) ─────────────────────────── */
  const HeaderFilterRow = () => (
    <div style={{ ...S.headerRow2, padding: "12px 16px 8px", alignItems: "center", gap: 8 }}>
      {/* logo */}
      <div onClick={() => navigate(`/MetaCommerce/${userId}`)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0, userSelect: "none" }}>
        <div style={{ padding: 2, borderRadius: 12, background: T.grad, display: "flex" }}>
          <img src={logo} alt="CosMeta" style={{ width: 28, height: 28, borderRadius: 9, background: "#fff", objectFit: "contain", padding: 4 }}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
        </div>
        <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, background: T.gradText, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>CosMeta</span>
      </div>

      {/* location */}
      {locationLoading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,.08)", borderRadius: 7, padding: "6px 10px", flexShrink: 0 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.blueLight, animation: "mc-pulse 1.2s infinite" }} />
          <span style={{ fontSize: 11, color: "rgba(255,255,255,.5)" }}>Locating…</span>
        </div>
      ) : locationDenied ? (
        <button style={{ ...S.hTextBtn, color: "#FCA5A5", borderColor: "rgba(239,68,68,.3)", background: "rgba(239,68,68,.1)" }}><MapPin size={12} /></button>
      ) : (
        <button onClick={handleToggleGlobal} style={{ ...S.hTextBtn, color: showGlobal ? "rgba(255,255,255,.55)" : T.blueLight, borderColor: showGlobal ? "rgba(255,255,255,.18)" : "rgba(91,203,255,.35)", background: showGlobal ? "rgba(255,255,255,.06)" : "rgba(91,203,255,.10)" }}>
          {showGlobal ? <Globe size={12} /> : <MapPin size={12} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div style={{ flex: 1 }} />

      {!isMobile && (
        <>
          <button onClick={() => navigate(`/metareorder/${userId}`)} style={S.hTextBtn}><RefreshCw size={13} color={T.gold} /> Buy Again</button>
          <button onClick={() => go("metaonboarding")} style={S.hTextBtn}><Tag size={13} color={T.blueLight} /> Sell</button>
          <button onClick={() => go("CustomerSupport")} style={S.hTextBtn}><SupportAgent style={{ fontSize: 14 }} /> Support</button>
        </>
      )}

      <button style={S.catBtn} onClick={() => isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v)}>
        <Menu size={14} /><span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
      </button>

      <button ref={sortBtnRef} onClick={toggleSort} style={{ ...S.filterChip, background: sortOpen ? T.blue : "rgba(255,255,255,.10)", borderColor: sortOpen ? T.blue : "rgba(255,255,255,.18)" }}>
        <SlidersHorizontal size={12} />
        <span style={{ display: isMobile ? "none" : "inline" }}>{activeSort !== "default" ? SORT_OPTS.find(o => o.key === activeSort)?.label?.split(":")[0] : "Sort"}</span>
        {sortOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {locationActive && (
        <button onClick={handleToggleGlobal} style={{ ...S.filterChip, color: showGlobal ? "rgba(255,255,255,.65)" : T.blueLight, borderColor: showGlobal ? "rgba(255,255,255,.18)" : "rgba(91,203,255,.4)", background: showGlobal ? "rgba(255,255,255,.08)" : "rgba(91,203,255,.12)" }}>
          <Globe size={12} /><span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : "Local"}</span>
        </button>
      )}

      {(activeProductGroup || activeSort !== "default") && (
        <button onClick={resetFilters} style={{ ...S.filterChip, color: T.gold, borderColor: "rgba(254,186,2,.35)", background: "rgba(254,186,2,.12)" }}><X size={11} /></button>
      )}

      <button onClick={() => setNavOpen(v => !v)} style={S.hTextBtn}>{navOpen ? <X size={18} /> : <Menu size={18} />}</button>

      {sortOpen && sortAnchor && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => { setSortOpen(false); setSortAnchor(null); }} />
          <div style={{ ...S.sortMenu, top: sortAnchor.top, left: sortAnchor.left }}>
            {SORT_OPTS.map(opt => (
              <div key={opt.key} onClick={() => { setActiveSort(opt.key); setSortOpen(false); setSortAnchor(null); }}
                style={{ padding: "11px 18px", fontSize: 13, fontWeight: activeSort === opt.key ? 700 : 500, color: activeSort === opt.key ? T.blue : NAVY, background: activeSort === opt.key ? T.bgMuted : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #F1F5F9" }}>
                {opt.label}{activeSort === opt.key && <span style={{ color: T.blue, fontWeight: 900 }}>✓</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  /* ─── HEADER ROW 2 (SearchRow) ─────────────────────────── */
  const HeaderSearchRow = () => (
    <div style={{ ...S.headerRow1, padding: "0 16px 10px", gap: 8 }}>
      <div style={{ ...S.searchWrap, flex: 1, borderColor: searchFocused ? "rgba(91,203,255,.7)" : "rgba(255,255,255,.15)", boxShadow: searchFocused ? "0 0 0 3px rgba(10,102,194,.2)" : "none" }}>
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} placeholder="Search products…" style={S.searchInput} />
        {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "rgba(255,255,255,.5)" }}><X size={14} /></button>}
        <button style={S.searchBtn}><Search size={17} color="#fff" /></button>
      </div>

      <div style={{ position: "relative", cursor: "pointer", flexShrink: 0 }} onClick={() => go("notifications")}>
        <Notifications style={{ fontSize: 24, color: T.blueLight }} />
        {unreadCount > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: T.globalRed, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{unreadCount}</span>}
      </div>

      <button onClick={goToCheckout} style={{ ...S.hTextBtn, position: "relative", background: cartTotal > 0 ? T.blue : "rgba(255,255,255,.08)", borderColor: cartTotal > 0 ? "transparent" : "rgba(255,255,255,.2)" }}>
        <ShoppingCart size={18} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: "#EF4444", color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal > 99 ? "99+" : cartTotal}</span>}
      </button>

      {!isMobile && (
        <>
          <button onClick={() => navigate(`/MetaClientDashboard/${userId}`)} style={S.hTextBtn}><ShoppingBag size={16} /><span>Orders</span></button>
          <button onClick={() => go("profile")} style={S.hTextBtn}><User size={16} /></button>
        </>
      )}
    </div>
  );

  /* ─── NAV DROPDOWN ─────────────────────────────────────── */
  const NavDropdown = () => (
    <AnimatePresence>
      {navOpen && (
        <>
          <div style={S.navOverlay as React.CSSProperties} onClick={() => setNavOpen(false)} />
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} style={S.navMenu}>
            {[
              { label: "Profile",  icon: <User size={15} />,          path: `/profile/${userId}` },
              { label: "Orders",   icon: <ShoppingBag size={15} />,    path: `/MetaClientDashboard/${userId}` },
              { label: "Sell",     icon: <Tag size={15} />,            path: `/metaonboarding/${userId}` },
              { label: "Support",  icon: <SupportAgent style={{ fontSize: 15 }} />, path: `/CustomerSupport/${userId}` },
              { label: "Chat",     icon: <ChatBubble style={{ fontSize: 15 }} />,   path: "/MessagesPreview" },
            ].map(item => (
              <button key={item.label} style={S.navItem} onClick={() => { navigate(item.path); setNavOpen(false); }}>
                <span style={{ color: T.blue }}>{item.icon}</span>{item.label}
              </button>
            ))}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ─── GC-STYLE SIDEBAR ACCORDION TREE ──────────────────── */
  const ElectronicsCategoryTree = () => (
    <div style={A.commerceCard}>
      <div style={A.commerceHeader}>Electronics</div>
      {electronicsCategories
        .filter((cat: any) => visibleIds.categories.has(cat.id))
        .map((cat: any) => {
          const isCatOpen = openCategories === cat.id;
          return (
            <div key={cat.id} style={A.category}>
              <div style={A.row} onClick={() => setOpenCategories(isCatOpen ? null : cat.id)}>
                {cat.icon && <cat.icon size={18} />}
                {cat.name}
                <motion.div animate={{ rotate: isCatOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}><ChevronRight size={16} /></motion.div>
              </div>
              <AnimatePresence>
                {isCatOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                    <div style={A.sub}>
                      {electronicsSubcategories
                        .filter((s: any) => s.parentId === cat.id && visibleIds.subs.has(s.id))
                        .map((sub: any) => {
                          const isSubOpen = openSubCategories === sub.id;
                          return (
                            <div key={sub.id}>
                              <div style={A.row} onClick={() => setOpenSubCategories(isSubOpen ? null : sub.id)}>
                                {sub.icon && <sub.icon size={16} />}
                                {sub.name}
                                <motion.div animate={{ rotate: isSubOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}><ChevronRight size={14} /></motion.div>
                              </div>
                              <AnimatePresence>
                                {isSubOpen && (
                                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                                    {electronicsProducts
                                      .filter((p: any) => p.parentId === sub.id && visibleIds.products.has(p.id))
                                      .map((p: any) => (
                                        <div key={p.id}
                                          style={{ ...A.productGroup, color: activeProductGroup === p.id ? T.blue : "#555" }}
                                          onClick={() => { setActiveProductGroup(p.id); closePanel(); }}>
                                          {p.icon && <p.icon size={14} />} {p.name}
                                        </div>
                                      ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
    </div>
  );

  /* ─── MOBILE CATEGORY BOTTOM SHEET (Glam pattern) ──────── */
  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(6,13,31,.45)", zIndex: 400 }} />
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: T.bg, zIndex: 401, borderTop: `1px solid ${T.border}`, boxShadow: "0 -20px 60px rgba(10,102,194,.12)", overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: T.border, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${T.border}`, padding: "12px 20px 10px" }}>
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.blue }}>⚡ CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}>
              <ElectronicsCategoryTree />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ─── SHOP BY CATEGORY — exact GeneralCommerce design ──── */
const CATEGORY_ROUTES: Record<number, string> = {
  20000001: "/Phones",
  20000020: "/Tablets",
  20000002: "/Computers",
  20000021: "/Laptops",
  20000003: "/Televisions",
  20000022: "/HomeEntertainment",
  20000004: "/Audio",
  20000005: "/Cameras",
  20000023: "/Film",
  20000006: "/Gaming",
  20000007: "/HomeAppliances",
  20000008: "/KitchenElectronics",
  20000009: "/Networking",
  20000024: "/SmartHome",
  20000010: "/CarElectronics",
  20000011: "/Tools",
  20000025: "/Accessories",
  20000012: "/OfficeElectronics",
  20000014: "/Security",
  20000015: "/Wearables",
  20000016: "/GPS",
  20000017: "/eBookReaders",
};




const ShopByCategory = () => {
    const getCatPhoto = (catId: number): string | null => {
      const l = filteredListings.find((l: any) => l.categoryId === catId && l.photos?.[0]);
      return l?.photos?.[0] ?? null;
    };

    // Define the grouped cards — each group of 4 tiles
    const SHOP_CARDS: { title: string; items: { id: number; name: string; icon: any; color: string }[] }[] = [
      {
        title: "Phones, Tablets, Laptops, Computers",
        items: [
          { id: 20000001, name: "Phones",    icon: electronicsCategories.find(c => c.id === 20000001)?.icon, color: "#1976D2" },
          { id: 20000020, name: "Tablets",   icon: electronicsCategories.find(c => c.id === 20000020)?.icon, color: "#1565C0" },
          { id: 20000021, name: "Laptops",   icon: electronicsCategories.find(c => c.id === 20000021)?.icon, color: "#6A1B9A" },
          { id: 20000002, name: "Computers", icon: electronicsCategories.find(c => c.id === 20000002)?.icon, color: "#7B1FA2" },
        ],
      },
      {
        title: "TVs, Audio, Gaming, Entertainment",
        items: [
          { id: 20000003, name: "TVs",              icon: electronicsCategories.find(c => c.id === 20000003)?.icon, color: "#E64A19" },
          { id: 20000022, name: "Entertainment",    icon: electronicsCategories.find(c => c.id === 20000022)?.icon, color: "#BF360C" },
          { id: 20000004, name: "Audio",            icon: electronicsCategories.find(c => c.id === 20000004)?.icon, color: "#009688" },
          { id: 20000006, name: "Gaming",           icon: electronicsCategories.find(c => c.id === 20000006)?.icon, color: "#0288D1" },
        ],
      },
      {
        title: "Cameras, Wearables, Smart Home, GPS",
        items: [
          { id: 20000005, name: "Cameras",    icon: electronicsCategories.find(c => c.id === 20000005)?.icon, color: "#455A64" },
          { id: 20000015, name: "Wearables",  icon: electronicsCategories.find(c => c.id === 20000015)?.icon, color: "#AD1457" },
          { id: 20000024, name: "Smart Home", icon: electronicsCategories.find(c => c.id === 20000024)?.icon, color: "#2E7D32" },
          { id: 20000016, name: "GPS",        icon: electronicsCategories.find(c => c.id === 20000016)?.icon, color: "#546E7A" },
        ],
      },
      {
        title: "Appliances, Kitchen, Security, Car",
        items: [
          { id: 20000007, name: "Appliances", icon: electronicsCategories.find(c => c.id === 20000007)?.icon, color: "#4CAF50" },
          { id: 20000008, name: "Kitchen",    icon: electronicsCategories.find(c => c.id === 20000008)?.icon, color: "#8E44AD" },
          { id: 20000014, name: "Security",   icon: electronicsCategories.find(c => c.id === 20000014)?.icon, color: "#C62828" },
          { id: 20000010, name: "Car",        icon: electronicsCategories.find(c => c.id === 20000010)?.icon, color: "#616161" },
        ],
      },
      {
        title: "Office, Portable, Tools, Accessories",
        items: [
          { id: 20000012, name: "Office",       icon: electronicsCategories.find(c => c.id === 20000012)?.icon, color: "#5C6BC0" },
          { id: 20000011, name: "Tools",        icon: electronicsCategories.find(c => c.id === 20000011)?.icon, color: "#FBC02D" },
          { id: 20000025, name: "Accessories",  icon: electronicsCategories.find(c => c.id === 20000025)?.icon, color: "#F9A825" },
          { id: 20000017, name: "eBooks",    icon: electronicsCategories.find(c => c.id === 20000017)?.icon, color: "#6D4C41" },

        ],
      },
   
    ];

    return (
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: NAVY, letterSpacing: -0.3 }}>Shop by Category</span>
        </div>

        <div style={isMobile
          ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 6 }
          : { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 18 }
        }>
          {SHOP_CARDS.map((group) => (
            <div key={group.title} style={{
              flexShrink: 0,
              ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}),
              background: "#fff",
              borderRadius: 20,
              overflow: "hidden",
              border: T.orangeOutline,
              boxShadow: `0 6px 28px ${T.blueDim}`,
            }}>
              {/* Card title */}
              <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 14, color: NAVY, letterSpacing: -0.2 }}>
                {group.title}
              </div>

              {/* 2×2 tile grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "0 8px 8px" : "0 10px 10px" }}>
                {group.items.map((cat) => {
                  const photo = getCatPhoto(cat.id);
                  const route = CATEGORY_ROUTES[cat.id];
                  const Icon  = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => route ? navigate(route) : setActiveProductGroup(cat.id)}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, borderRadius: isMobile ? 10 : 14, overflow: "hidden" }}
                    >
                      <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: isMobile ? 10 : 14, overflow: "hidden", position: "relative", background: `linear-gradient(135deg,${cat.color}22,${cat.color}08)` }}>
                        {photo ? (
                          <img src={photo} alt={cat.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .35s cubic-bezier(.34,1.56,.64,1)" }}
                            onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.10)")}
                            onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {Icon && <Icon size={isMobile ? 26 : 34} color={cat.color} />}
                          </div>
                        )}
                        {/* gradient overlay */}
                        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${cat.color}EE,transparent 50%)` }} />
                        <span style={{ position: "absolute", bottom: isMobile ? 4 : 6, left: 0, right: 0, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>
                          {cat.name}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* See all footer */}
              <button
                onClick={resetFilters}
                style={{ width: "100%", padding: isMobile ? "7px 12px" : "9px 16px", background: "none", border: "none", borderTop: `1.5px solid ${T.blueDim}`, cursor: "pointer", fontSize: isMobile ? 11 : 13, fontWeight: 700, color: T.blue, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {group.title} <ArrowRight size={isMobile ? 12 : 14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };


  /* ─── PRODUCT CARD — full-image overlay ────────────────── */
const ProductCard = ({ item, isMob }: { item: any; isMob: boolean }) => {
    const qty      = getQty(item.listingId);
const price = `${symbol || "$"}${((item.rate ?? 0) * (rate ?? 1)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;    const discount = typeof item.discount === "number" && item.discount > 0 ? item.discount : null;
    const [hovered, setHovered] = useState(false);

    // ── MOBILE — keep existing tile style ──────────────────
    if (isMob) {
      return (
        <motion.div
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 360, damping: 26 }}
          style={{ borderRadius: 18, overflow: "hidden", border: T.orangeOutline, boxShadow: `0 4px 18px ${T.blueDim}`, background: "#fff", cursor: "pointer", display: "flex", flexDirection: "column" }}
          onClick={() => goToDetails(item.listingId)}
        >
          <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: `linear-gradient(135deg,${T.blue}22,${T.blue}08)` }}>
            {item.photos?.[0] ? (
              <img src={item.photos[0]} alt={item.name}
                style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .35s cubic-bezier(.34,1.56,.64,1)" }}
                onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.10)")}
                onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")} />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Package size={36} color={T.blue} />
              </div>
            )}
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${T.navy}EE,transparent 52%)` }} />
            {discount && (
              <div style={{ position: "absolute", top: 7, left: 7, background: "#EF4444", color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 6, zIndex: 5 }}>
                {discount}% OFF
              </div>
            )}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "8px 10px 10px", zIndex: 3, pointerEvents: "none" }}>
              <div style={{ fontWeight: 800, fontSize: 10, color: "#fff", lineHeight: 1.25, marginBottom: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>
                {item.name}
              </div>
<span style={{ color: T.gold, fontWeight: 900, fontSize: 16, textShadow: "0 1px 0 rgba(120,80,0,.9)", letterSpacing: -0.3 }}>{price}</span>            </div>
            <div style={{ position: "absolute", top: 7, right: 7, zIndex: 10 }} onClick={e => e.stopPropagation()}>
              {qty > 0 ? (
                <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  style={{ display: "flex", alignItems: "center", gap: 0, background: "rgba(0,97,254,.92)", borderRadius: 20, padding: "4px 6px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(255,255,255,.2)" }}>
                  <button style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                    onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                    <Minus size={9} color="#fff" />
                  </button>
                  <span style={{ color: "#fff", fontWeight: 900, minWidth: 18, textAlign: "center", fontSize: 11 }}>{qty}</span>
                  <button style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                    onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                    <Plus size={9} color="#fff" />
                  </button>
                </motion.div>
              ) : (
        <motion.button whileTap={{ scale: 0.88 }}
  onClick={e => { e.stopPropagation(); handleAddToCart(item); }}
  style={{ width: 50, height: 50, borderRadius: "50%", border: "none", background: "#E50914", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 10px rgba(239,68,68,.6)" }}>
<ShoppingCartOutlined style={{ fontSize: 22, color: "#fff" }} />
</motion.button>
              )}
            </div>
          </div>
        </motion.div>
      );
    }

    // ── DESKTOP — category-card style with big Euronics price ──
    return (
      <motion.div
        whileHover={{ y: -5, boxShadow: `0 20px 48px rgba(10,102,194,.18), 0 0 0 2px ${T.blue}55` }}
        whileTap={{ scale: 0.96 }}
        transition={{ type: "spring", stiffness: 340, damping: 26 }}
        style={{ borderRadius: 20, overflow: "hidden", border: T.orangeOutline, boxShadow: `0 6px 28px ${T.blueDim}`, background: "#fff", cursor: "pointer", display: "flex", flexDirection: "column" }}
        onClick={() => goToDetails(item.listingId)}
      >
        <div
          style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: `linear-gradient(135deg,${T.blue}18,${T.blue}06)` }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {item.photos?.[0] ? (
            <img src={item.photos[0]} alt={item.name}
              style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .38s cubic-bezier(.34,1.56,.64,1)", transform: hovered ? "scale(1.08)" : "scale(1)" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={44} color={T.blue} style={{ opacity: 0.35 }} />
            </div>
          )}
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${NAVY}F0 0%,${NAVY}88 38%,transparent 68%)`, pointerEvents: "none" }} />
          {discount && (
            <div style={{ position: "absolute", top: 8, left: 8, background: "#EF4444", color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 7, zIndex: 5, boxShadow: "0 2px 8px rgba(239,68,68,.5)" }}>
              {discount}% OFF
            </div>
          )}
          {/* + / qty — top right */}
          <div style={{ position: "absolute", top: 8, right: 8, zIndex: 10 }} onClick={e => e.stopPropagation()}>
            {qty > 0 ? (
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                style={{ display: "flex", alignItems: "center", background: "rgba(0,97,254,.92)", borderRadius: 22, padding: "3px 5px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(255,255,255,.22)", gap: 0 }}>
                <button style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                  onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                  <Minus size={10} color="#fff" />
                </button>
                <span style={{ color: "#fff", fontWeight: 900, minWidth: 22, textAlign: "center", fontSize: 13 }}>{qty}</span>
                <button style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                  onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                  <Plus size={10} color="#fff" />
                </button>
              </motion.div>
            ) : (
              <motion.button whileHover={{ scale: 1.12 }} whileTap={{ scale: 0.88 }}
                onClick={e => { e.stopPropagation(); handleAddToCart(item); }}
                style={{ width: 50, height: 50, borderRadius: "50%", border: "none", background: T.gold, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 3px 12px rgba(254,186,2,.65)" }}>
                <ShoppingCart size={16} color={NAVY} />
              </motion.button>
            )}
          </div>
          {/* name + BIG price */}
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 14px 14px", zIndex: 3, pointerEvents: "none" }}>
            <div style={{ fontWeight: 800, fontSize: 12, color: "#fff", lineHeight: 1.25, marginBottom: 5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", textShadow: "0 1px 4px rgba(0,0,0,.6)", letterSpacing: -0.2 }}>
              {item.name}
            </div>
            <span style={{ color: T.gold, fontWeight: 900, fontSize: 22, letterSpacing: -0.5, textShadow: "0 1px 0 rgba(120,80,0,.8), 0 2px 12px rgba(254,186,2,.5)", lineHeight: 1 }}>
              {price}
            </span>
          </div>
        </div>
      </motion.div>
    );
  };














  /* ─── RENDER PRODUCTS ───────────────────────────────────── */
  const renderProducts = (isMob: boolean) => (
    <div style={S.productsWrap}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: GRADIENT }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: NAVY }}>
            {activeProductGroup ? "Filtered" : locationDenied ? "All Products" : showGlobal ? "Global" : userCity ? `Near ${userCity}` : userCountry ?? "All Products"}
          </span>
          <span style={{ fontSize: 12, color: "#94A3B8", background: "#F1F5F9", padding: "2px 9px", borderRadius: 20 }}>{filteredProducts.length}</span>
        </div>
        {locationActive && (
          <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "#F1F5F9" : "#EFF6FF", borderRadius: 20, padding: "5px 13px", border: `1.5px solid ${showGlobal ? "#E2E8F0" : "#BFDBFE"}`, fontSize: 11, fontWeight: 700, color: showGlobal ? "#64748B" : "#2563EB", cursor: "pointer" }}>
            {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
          </button>
        )}
      </div>

      {filteredProducts.length > 0 ? (
        <div style={isMob ? S.mobileGrid : S.grid}>
          {filteredProducts.map((item: any) => <ProductCard key={item.listingId} item={item} isMob={isMob} />)}
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 56 }}>
          <div style={{ fontSize: 52, marginBottom: 14 }}>⚡</div>
          <h3 style={{ fontWeight: 900, color: NAVY, marginBottom: 8 }}>{locationDenied ? "Location needed" : "No products found"}</h3>
          <p style={{ color: "#94A3B8", marginBottom: 24, fontSize: 13 }}>{locationDenied ? "Enable location to see local products." : "No products match your current filters."}</p>
          <button onClick={() => locationDenied ? setLocationModalOpen(true) : resetFilters()}
            style={{ padding: "12px 28px", borderRadius: 12, border: "none", background: GRADIENT, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
            {locationDenied ? "Enable Location" : "View All Products"}
          </button>
        </div>
      )}
    </div>
  );

  /* ─── VARIANT MODAL ─────────────────────────────────────── */
  const VariantModal = () => (
    <AnimatePresence>
      {variantOpen && variantItem && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setVariantOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 999, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "28px 28px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ width: 44, height: 4, borderRadius: 2, background: "#E2E8F0", margin: "0 auto 22px" }} />
            <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
              {variantItem.photos?.[0] && (
                <div style={{ width: 76, height: 76, borderRadius: 14, overflow: "hidden", background: "#F8FAFC", flexShrink: 0 }}>
                  <img src={variantItem.variants?.[previewColorId!]?.imageUrl ?? variantItem.photos[0]} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
              )}
              <div>
                <h3 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 16, color: NAVY }}>{variantItem.name}</h3>
                <span style={{ color: T.blue, fontWeight: 900, fontSize: 18 }}>{symbol || "$"}{((variantItem.rate ?? 0) * (rate ?? 1)).toFixed(2)}</span>
              </div>
            </div>
            {variantMode && (
              <>
                <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8 }}>
                  {variantMode === "beauty" ? "Select Shade" : "Select Color"}
                </h4>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                  {Object.keys(variantItem.variants || {}).map(id => {
                    const colorId = Number(id);
                    const color   = electronicsColorAttributes.values.find((v: any) => v.id === colorId);
                    return (
                      <button key={colorId}
                        onClick={() => { setSelectedColor(colorId); if (variantMode === "fashion") setSelectedSize(null); }}
                        style={{ width: 34, height: 34, borderRadius: "50%", border: selectedColor === colorId ? `3px solid ${NAVY}` : "2px solid #ddd", background: color?.hexCodes?.[0] ?? "#eee", cursor: "pointer", transform: selectedColor === colorId ? "scale(1.15)" : "scale(1)", boxShadow: selectedColor === colorId ? `0 0 0 2px #fff,0 0 0 4px ${T.blue}` : "none", transition: "all .15s" }} />
                    );
                  })}
                </div>
                {variantMode === "fashion" && selectedColor && (
                  <>
                    <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8 }}>Select Size</h4>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                      {(variantItem.variants?.[selectedColor]?.sizeIds ?? []).map((sizeId: number) => {
                        const size = electronicsSizeAttributes.values.find((v: any) => v.id === sizeId);
                        return (
                          <button key={sizeId} onClick={() => setSelectedSize(sizeId)}
                            style={{ padding: "8px 16px", borderRadius: 10, border: `2px solid ${selectedSize === sizeId ? NAVY : "#E2E8F0"}`, fontWeight: 700, fontSize: 13, cursor: "pointer", background: selectedSize === sizeId ? NAVY : "#fff", color: selectedSize === sizeId ? "#fff" : NAVY, transition: "all .15s" }}>
                            {size?.name}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
                {selectedColor && (variantMode === "beauty" || (variantMode === "fashion" && selectedSize)) && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginBottom: 20, background: "#F8FAFC", borderRadius: 14, padding: "12px 16px" }}>
                    <button onClick={() => setVariantQty(q => Math.max(1, q - 1))} style={{ width: 38, height: 38, borderRadius: 10, border: "2px solid #E2E8F0", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" }}><Minus size={14} color={NAVY} /></button>
                    <span style={{ fontSize: 22, fontWeight: 900, color: NAVY, minWidth: 30, textAlign: "center" }}>{variantQty}</span>
                    <button onClick={() => setVariantQty(q => q + 1)} style={{ width: 38, height: 38, borderRadius: 10, border: `2px solid ${T.blue}`, background: T.blue, display: "grid", placeItems: "center", cursor: "pointer" }}><Plus size={14} color="#fff" /></button>
                  </div>
                )}
              </>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(false)} style={{ flex: 1, padding: 14, borderRadius: 14, border: `2px solid ${canContinue ? NAVY : "#E2E8F0"}`, background: canContinue ? NAVY : "#F8FAFC", color: canContinue ? "#fff" : "#94A3B8", fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>Add to Cart</button>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(true)} style={{ flex: 1, padding: 14, borderRadius: 14, border: "none", background: canContinue ? GRADIENT : "#F8FAFC", color: canContinue ? "#fff" : "#94A3B8", fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>Buy Now</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ─── LOCATION MODAL ────────────────────────────────────── */
  const LocationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }}
            onClick={e => e.stopPropagation()}
            style={{ width: 320, borderRadius: 20, padding: 24, background: T.bg, boxShadow: "0 30px 80px rgba(0,0,0,.25)", textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: GRADIENT, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontWeight: 900, marginBottom: 8, color: NAVY }}>Location Disabled</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>Enable location in your browser settings to see local products.</p>
            <button onClick={() => setLocationModalOpen(false)} style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: GRADIENT, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Got it</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ─── MOBILE BOTTOM TABS ────────────────────────────────── */
  const BottomTabs = () => isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon: <Home size={22} />,         label: "Home",    action: () => navigate(`/MetaCommerce/${userId}`) },
        { icon: <ShoppingBasket size={22} />,         label: "Stores",    action: () => navigate(`/MetaStores`) },

//        { icon: <ChatBubble style={{ fontSize: 22 }} />, label: "Chat", action: () => navigate("/MessagesPreview") },


        {
          label: "Cart",
          icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={22} />
              {cartItems.length > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: "#EF4444", color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartItems.reduce((s: number, i: any) => s + i.qty, 0)}</span>}
            </div>
          ),
          action: goToCheckout,
        },
        { icon: <ShoppingBag size={22} />,  label: "Orders",  action: () => navigate(`/MetaClientDashboard/${userId}`) },
        { icon: <User size={22} />,          label: "Profile", action: () => navigate(`/profile/${userId}`) },
      ].map(t => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action} style={{ ...S.tabItem, position: "relative" }}>
          {React.cloneElement(t.icon as any, { color: "#64748B" })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ─── RENDER ────────────────────────────────────────────── */
  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{display:none;}
        input,button{font-family:inherit;}
        @keyframes mc-pulse{0%,100%{opacity:1}50%{opacity:.3}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
        input::placeholder{color:rgba(255,255,255,.4);}
      `}</style>

      <AnnouncementBar />

      <div style={S.headerTop}>
        <HeaderFilterRow />
        <HeaderSearchRow />
      </div>

      <NavDropdown />
      <MobileCategorySheet />

      {/* ── DESKTOP: sidebar far left + main ── */}
      {!isMobile && (
        <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
          {desktopCatOpen && (
            <aside style={A.sidebar}>
              <ElectronicsCategoryTree />
            </aside>
          )}
          <main style={{ minWidth: 0 }}>
            <ShopByCategory />
            {renderProducts(false)}
          </main>
        </div>
      )}

      {/* ── MOBILE ── */}
      {isMobile && (
        <div style={{ padding: "14px 12px 0" }}>
          <ShopByCategory />
          {renderProducts(true)}
        </div>
      )}

      <VariantModal />
      <LocationModal />
      <BottomTabs />
    </div>
  );
};

export default MetaCommerce;